import { AuthService } from './authService';
import {
  CompletePutAwayLineRequest,
  CompletePutAwayLineResponse,
  CompletePutAwayBulkRequest,
  CompletePutAwayBulkResponse,
  CreatePutAwayRequest,
  CreatePutAwayResponse,
  StcIntRelOrderDto,
  StcIntRelOrderLineDto,
  StcIntRelOrderDetailsDto,
  UpdateQuantityMovedRequest,
  UpdateStcIntRelOrderStatusRequest
} from '../types/putaway';

import { throwIfLockConflict } from '../utils/lockConflictError';
import { extractApiError } from '../utils/apiError';

// Re-eksport: disse typene var tidligere duplisert her (kilde: types/putaway.ts).
// Beholdes som eksport så eksisterende `import { ... } from 'services/putAwayService'` virker.
export type {
  PutAwayReservationDto,
  CreatePutAwayRequest,
  CreatePutAwayResponse,
  StcIntRelOrderDto,
  StcIntRelOrderLineDto,
  StcIntRelOrderDetailsDto,
  UpdateQuantityMovedRequest,
  UpdateStcIntRelOrderStatusRequest,
  LockStcIntRelOrderRequest
} from '../types/putaway';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

/**
 * Response from running the standard put-away rule group over all open grunnlag
 * ("Opprett utlegg" tool). Mirrors RunPutAwayResponse in BizClick.WMS.Shared.
 */
export interface RunPutAwayResponse {
  success: boolean;
  putAwayNeeded: boolean;
  stcOrderNo: number | null;
  stcOrderNos: number[];
  processedCount: number;
  totalLineCount: number;
  ruleGroupId: number;
  message: string | null;
  errorMessage: string | null;
}

// Interfaces for batch put-away from relocation (Grunnlag)
export interface CreatePutAwayFromRelocationRequest {
  relocationIds: string[];
}

export interface CreatePutAwayFromRelocationResponse {
  success: boolean;
  stcOrderNo?: number;
  processedCount: number;
  failedCount: number;
  successfulRelIds: string[];
  failures: PutAwayFailure[];
  errorMessage?: string;
  totalLineCount: number;
}

// Respons fra sletting av utplasseringsordre (grunnlaget frigis tilbake til Grunnlag-skjermen)
export interface DeletePutAwayOrderResponse {
  message: string;
  releasedGrunnlagCount: number;
}

// Respons fra regenerering av utplasseringsordre fra grunnlag.
// success=false med deletedOrderNo satt betyr: ordren ble slettet, men regenereringen feilet —
// grunnlaget ligger da tilgjengelig i Grunnlag-skjermen for manuell generering.
export interface RegeneratePutAwayOrderResponse {
  success: boolean;
  deletedOrderNo: number;
  deletedLineCount: number;
  releasedGrunnlagCount: number;
  newOrderNo?: number;
  newLineCount: number;
  processedCount: number;
  failedCount: number;
  errorMessage?: string;
}

export interface PutAwayFailure {
  relId: string;
  itemNo: string;
  errorMessage: string;
}

class PutAwayService {
  
  /**
   * Get all available put-away orders (IntRelOrders) by status
   */
  async getAvailablePutAwayOrders(): Promise<StcIntRelOrderDto[]> {
    try {
      // Get orders with status indicating they're ready for put-away
      // Using status 10 (assigned) as similar to picking status logic
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/intrelorders/status/10`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to get put-away orders: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting available put-away orders:', error);
      throw error;
    }
  }

  /**
   * Get put-away order details with all lines
   */
  async getPutAwayOrderDetails(stcOrderNo: number): Promise<StcIntRelOrderDetailsDto> {
    try {
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/details`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to get put-away order details: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting put-away order details:', error);
      throw error;
    }
  }

  /**
   * Lock a put-away order to current operator
   */
  async lockPutAwayOrder(stcOrderNo: number, operatorID?: string): Promise<void> {
    const body = operatorID ? { operatorID } : {};

    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/intrelorders/${stcOrderNo}/lock`,
      body
    );

    if (!response.ok) {
      await throwIfLockConflict(response);
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to lock put-away order'));
    }
  }

  /**
   * Unlock a put-away order
   */
  async unlockPutAwayOrder(stcOrderNo: number, operatorID?: string): Promise<void> {
    try {
      const body = operatorID ? { operatorID } : {};
      
      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/unlock`,
        body
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(extractApiError(errorData, response, 'Failed to unlock put-away order'));
      }
    } catch (error) {
      console.error('Error unlocking put-away order:', error);
      throw error;
    }
  }

  /**
   * Delete a put-away order (manager/administrator only).
   * Grunnlag records are released back to the Grunnlag screen (LineStatus 30 -> 20).
   */
  async deletePutAwayOrder(stcOrderNo: number): Promise<DeletePutAwayOrderResponse> {
    const response = await AuthService.authenticatedDelete(
      `${API_BASE_URL}/intrelorders/${stcOrderNo}`
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to delete put-away order'));
    }

    return await response.json();
  }

  /**
   * Delete and regenerate a put-away order from its grunnlag via the rule engine
   * (manager/administrator only). Used after grunndata changes (e.g. bin capacity)
   * so the order gets a new bin split.
   */
  async regeneratePutAwayOrder(stcOrderNo: number): Promise<RegeneratePutAwayOrderResponse> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/putaway/orders/${stcOrderNo}/regenerate`,
      {}
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to regenerate put-away order'));
    }

    return await response.json();
  }

  /**
   * Update quantity moved for a specific put-away line
   */
  async updateQuantityMoved(stcOrderNo: number, stcLineNo: number, quantityMoved: number, movedBy?: string): Promise<void> {
    try {
      const request: UpdateQuantityMovedRequest = {
        quantityMoved,
        movedBy
      };

      const response = await AuthService.authenticatedPut(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/lines/${stcLineNo}/quantity`,
        request
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(extractApiError(errorData, response, 'Failed to update quantity moved'));
      }
    } catch (error) {
      console.error('Error updating quantity moved:', error);
      throw error;
    }
  }

  /**
   * Update put-away order status
   */
  async updatePutAwayOrderStatus(stcOrderNo: number, status: number): Promise<void> {
    try {
      const request: UpdateStcIntRelOrderStatusRequest = { status };

      const response = await AuthService.authenticatedPut(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/status`,
        request
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(extractApiError(errorData, response, 'Failed to update put-away order status'));
      }
    } catch (error) {
      console.error('Error updating put-away order status:', error);
      throw error;
    }
  }

  /**
   * Update put-away order line (for updating bin codes, etc.)
   */
  async updatePutAwayOrderLine(stcOrderNo: number, stcLineNo: number, updates: Partial<StcIntRelOrderLineDto>): Promise<StcIntRelOrderLineDto> {
    try {
      const response = await AuthService.authenticatedPut(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/lines/${stcLineNo}`,
        updates
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(extractApiError(errorData, response, 'Failed to update put-away order line'));
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating put-away order line:', error);
      throw error;
    }
  }

  /**
   * Update only the destination bin (ToBinCode) for a put-away line.
   * Used after capacity registration to re-suggest a new location for the rest-line.
   */
  async updateLineDestination(stcOrderNo: number, stcLineNo: number, toBinCode: string): Promise<void> {
    const response = await AuthService.authenticatedFetch(
      `${API_BASE_URL}/intrelorders/${stcOrderNo}/lines/${stcLineNo}/destination`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ toBinCode }),
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to update line destination'));
    }
  }

  /**
   * Start put-away process (similar to start picking)
   */
  async startPutAway(stcOrderNo: number): Promise<void> {
    try {
      // Update status to "Under arbeid" (20) - similar to picking
      await this.updatePutAwayOrderStatus(stcOrderNo, 20);
    } catch (error) {
      console.error('Error starting put-away:', error);
      throw error;
    }
  }

  /**
   * Complete put-away process (Fullfør utplassering)
   * Sets all line statuses to 60 and OrderStatus to 30 (Fullført)
   */
  async completePutAway(stcOrderNo: number): Promise<void> {
    try {
      // Call backend endpoint that handles full completion:
      // - Updates all lines to LineStatus = 60
      // - Updates order to OrderStatus = 30 (Fullført)
      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/finalize`,
        {}
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(extractApiError(errorData, response, 'Failed to complete put-away'));
      }
    } catch (error) {
      console.error('Error completing put-away:', error);
      throw error;
    }
  }

  /**
   * Complete a single put-away line with bin location and quantity
   * This updates both the line and ItemBins inventory via domain events
   * Also creates StcTransaction records for inventory tracking
   */
  async completePutAwayLineWithBin(
    stcOrderNo: number,
    stcLineNo: number,
    warehouseID: string,
    toBinCode: string,
    quantity: number
  ): Promise<CompletePutAwayLineResponse> {
    try {
      const request: CompletePutAwayLineRequest = {
        warehouseID,
        toBinCode,
        quantity
      };

      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/lines/${stcLineNo}/complete-with-bin`,
        request
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(extractApiError(errorData, response, 'Failed to complete put-away line'));
      }

      return await response.json();
    } catch (error) {
      console.error('Error completing put-away line with bin:', error);
      throw error;
    }
  }

  /**
   * Complete all pending lines in a put-away order to the same bin location
   * Bulk operation that updates ItemBins for all items via domain events
   * Also creates StcTransaction records for each line
   */
  async completePutAwayBulkToBin(
    stcOrderNo: number,
    warehouseID: string,
    toBinCode: string
  ): Promise<CompletePutAwayBulkResponse> {
    try {
      const request: CompletePutAwayBulkRequest = {
        warehouseID,
        toBinCode
      };

      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/complete-all-to-bin`,
        request
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(extractApiError(errorData, response, 'Failed to complete bulk put-away'));
      }

      return await response.json();
    } catch (error) {
      console.error('Error completing bulk put-away to bin:', error);
      throw error;
    }
  }

  /**
   * Get available put-away orders for operator (locked to current operator or available)
   */
  async getAvailablePutAwayOrdersForOperator(): Promise<StcIntRelOrderDto[]> {
    try {
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/intrelorders/available`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to get available put-away orders for operator: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting available put-away orders for operator:', error);
      throw error;
    }
  }

  /**
   * Run the standard put-away rule group (SystemSetup.StdPutAwayRuleGroupID) over ALL
   * open grunnlag — the "Opprett utlegg" tool. Manager/administrator only.
   */
  async runPutAway(): Promise<RunPutAwayResponse> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/putaway/run`,
      {}
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      // 422/404 carry a RunPutAwayResponse body with errorMessage — surface it
      if (errorData && typeof errorData.success === 'boolean') {
        return errorData as RunPutAwayResponse;
      }
      throw new Error(extractApiError(errorData, response, 'Kunne ikke opprette utlegg'));
    }

    return await response.json();
  }

  /**
   * Create put-away orders from StcIntRelocation records (Grunnlag)
   * This implements the "Opprett utlegg på grunnlag" functionality
   */
  async createPutAwayFromRelocationBatch(relocationIds: string[]): Promise<CreatePutAwayFromRelocationResponse> {
    try {
      const request: CreatePutAwayFromRelocationRequest = {
        relocationIds
      };

      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/putaway/create-from-relocation-batch`,
        request
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(extractApiError(errorData, response, 'Failed to create put-away from relocation'));
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating put-away from relocation batch:', error);
      throw error;
    }
  }

  // EXISTING LEGACY PUT-AWAY FUNCTIONALITY - Keep for backwards compatibility
  /**
   * Kjører put-away logikk for en StcTrans linje (Legacy functionality)
   */
  async createPutAwayForStcIntRelOrderLine(request: CreatePutAwayRequest): Promise<CreatePutAwayResponse> {
    try {
      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/putaway/create-for-stc-int-rel-order-line`,
        request
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(extractApiError(errorData, response, 'Failed to create put-away'));
      }
      
      return await response.json();
    } catch (error) {
      console.error('Detaljert feil ved opprettelse av put-away:', error);
      console.error('Request data:', request);
      throw error;
    }
  }

  /**
   * Hjelpefunksjon for å bygge put-away request fra StcTrans data (Legacy functionality)
   * Now includes pOrderNo and pOrderLineNo for Standard mode to lookup StdLocationName
   */
  buildPutAwayRequest(
    stcTrans: any,
    companyNo: string,
    warehouseID: string,
    binGrpNo: number = 1,
    pickRuleNo: number = 1,
    putAwayToPickableBinYesNo: boolean = true
  ): CreatePutAwayRequest {
    return {
      companyNo,
      warehouseID,
      itemNo: stcTrans.itemNo,
      binGrpNo,
      pickRuleNo,
      grShipmentNo: stcTrans.grShipmentNo || 0,
      putAwayToPickableBinYesNo,
      quantity: stcTrans.quantity || 0,
      // For Standard mode - needed to lookup POrderLine.StdLocationName from ERP
      pOrderNo: stcTrans.orderNo || undefined,
      pOrderLineNo: stcTrans.lineNo || undefined
    };
  }
}

export const putAwayService = new PutAwayService();
